import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import FeatureBoxBase, * as FeatureBox from '@segment/matcha/components/FeatureBox'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import { DataCollectIcon, DataScaleIcon } from '@segment/matcha/components/Icons/Brandbook'

import IconWrapper from '@segment/matcha/components/IconWrapper'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'

import CollectDataDetailsImage from './CollectDataDetailsImage'

const copy = {
  heading: `Data collection from anywhere`,
  description: `Collect user events from any platform with our simple analytics API.`,
}

const features = [
  {
    heading: `Pull data from any platform`,
    description: `Send us data from any part of your product: mobile, web, or server. We have performant, open-source libraries for every platform.`,
    icon: <DataCollectIcon />,
  },
  {
    heading: `Enterprise-grade scale`,
    description: `25,000+ companies collect over 1 trillion events every month on our APIs.`,
    icon: <DataScaleIcon />,
  },
]

const CollectDataDetails: FC<PropsWithChildren<unknown>> = () => {
  return (
    <Wrapper>
      <SectionBase variant="2Col" copyVariant="narrow">
        <Section.Copy>
          <CopyTextBase>
            <ResponsiveHeading tag="h2" variant="h2">
              {copy.heading}
            </ResponsiveHeading>
            <CopyText.Description>{copy.description}</CopyText.Description>
            <CopyText.Link href="https://segment.com/docs/connections/spec/track/">Explore Tracking API</CopyText.Link>
          </CopyTextBase>
          <ColumnTrackWrapper>
            <ColumnTrack gap="small">
              {features.map((feature) => (
                <FeatureBoxBase key={feature.heading} alignment="left" headerPosition="left">
                  <FeatureBox.Header>
                    <IconWrapper elevation={1} variant="circle">
                      {feature.icon}
                    </IconWrapper>
                  </FeatureBox.Header>
                  <FeatureBox.Body>
                    <FeatureBox.Title>{feature.heading}</FeatureBox.Title>
                    <FeatureBox.Text>{feature.description}</FeatureBox.Text>
                  </FeatureBox.Body>
                </FeatureBoxBase>
              ))}
            </ColumnTrack>
          </ColumnTrackWrapper>
        </Section.Copy>
        <Section.Contents>
          <CollectDataDetailsImage />
        </Section.Contents>
      </SectionBase>
    </Wrapper>
  )
}

const Wrapper = styled.section`
  display: grid;
`

const ColumnTrackWrapper = styled.div`
  margin-top: 40px;
`

const ResponsiveHeading = styled(CopyText.Heading)`
  ${({ theme }) => theme.breakpoints.up(`lg`)} {
    max-width: 340px;
  }
`

export default CollectDataDetails
